<template>
    <div class="apple-pay-btn-container" v-if="canApplePayment">
        <div
            class="apple-pay-button apple-pay-button-black"
            @click="setButtonClickListener"
        >Apple Pay
        </div>
    </div>
</template>

<script>
import ApiRequest from "../../../../utils/api-request";
import {mapActions, mapGetters} from "vuex";
import CartMixin from "../../../../mixins/CartMixin";
import LoaderMixin from "../../../../mixins/LoaderMixin";

const BACKEND_URL_VALIDATE_SESSION = "https://{yourServer}/validateSession";
const BACKEND_URL_PAY = "https://{yourServer}/pay";

export default {
    name: "ApplePay",
    mixins: [CartMixin, LoaderMixin],
    props: {
        amount: {
            type: Number,
            default: 0
        },
        refContactInformation: {
            type: Object,
            default: null
        },
        referrer: {
            type: String,
            default: ''
        },
        isProduct: {
            type: Boolean,
            default: false
        },
        productShippingAddress: {
            type: String,
            default: ''
        },
        productVariantSelected: {
            type: String,
            default: ''
        },
        productShippingMethod: {
            type: String,
            default: ''
        },
        product: {
            type: Object,
            default: () => {
            }
        }
    },

    data: function () {
        return {
            payload: {},
            order_id: 0,
            currency: 'USD',
            hasClientData: true,
            productData: {
                isProduct: this.isProduct,
                referrer: this.referrer,
                productShippingMethod: this.productShippingMethod,
                productShippingAddress: this.productShippingAddress,
                productVariantSelected: this.productVariantSelected
            },

            canApplePayment: false,
            payments: {
                acceptedCardSchemes: [
                    "amex",
                    "masterCard",
                    "maestro",
                    "visa",
                    "mada"
                ]
            },
            shop: {
                product_price: '1.0',
                shop_name: "Bike Rent NYC",
                shop_localisation: {
                    currencyCode: "USD",
                    countryCode: "US"
                },
                // shipping: {
                //     US_region: [
                //         {
                //             label: "Free Shipping",
                //             amount: "0.00",
                //             detail: "Arrives in 3-5 days",
                //             identifier: "freeShipping"
                //         },
                //         {
                //             label: "Express Shipping",
                //             amount: "1.00",
                //             detail: "Arrives in 1-2 days",
                //             identifier: "expressShipping"
                //         }
                //     ],
                //     WORLDWIDE_region: [
                //         {
                //             label: "Worldwide Standard Shipping",
                //             amount: "2.00",
                //             detail: "Arrives in 5-8 days",
                //             identifier: "worldwideShipping"
                //         }
                //     ]
                // }
            }
        };
    },

    computed: {
        ...mapGetters('app', ['firstName', 'lastName', 'address2', 'email', 'phone']),
        ...mapGetters('filter', ['journeyDate']),
        ...mapGetters('cart', ['isEmpty', 'totalPrice', 'totalTax', 'totalDiscount']),
        ...mapGetters('cart', {
            usedPromoCode: 'promoCode',
            cartBikes: 'bikes',
            cartEvents: 'events',
            cartTours: 'tours',
            cartAccessories: 'accessories',
            cartMerchandises: 'merchandises',
            cartAdditionalOptions: 'additionalOptions'
        }),
    },

    mounted() {
        this.shop.product_price = this.amount;
        this.initApplePaySession();
    },

    methods: {
        ...mapActions('cart', ['usePromoCode']),
        ...mapActions('payment', ['initCreditCardPayment']),
        errorNotify(message) {
            if (message) {
                this.$notify({
                    text: message,
                    type: 'error'
                });
            } else {
                this.$notify({
                    type: 'error',
                    title: 'Server error!',
                    text: 'Something went wrong. Please contact our support service',
                    duration: 5000
                });
            }
        },

        createOrder(paymentToken) {
            if (this.refContactInformation) {
                if (!this.refContactInformation.validate()) {
                    return false;
                }
            } else {
                if (!this.$parent.validate()) {
                    return false;
                }
            }

            this.enableLoader();
            const payload = this.createPayload('apple-pay', this.productData);
            payload.paymentToken = paymentToken;

            new ApiRequest().createDigitalWalletOrder(payload).then((response) => {

                if (!response.data.status) {
                    this.errorNotify(response.data.message);
                    this.disableLoader();
                    return;
                }

                this.initCreditCardPayment();

                try {
                    gtag('event', 'conversion', {
                        'send_to': window.gtag_send_to,
                        'value': ((this.totalPrice + this.totalTax - this.totalDiscount) / 100),
                        'currency': 'USD',
                        'transaction_id': response.data.info.qr,
                        'ref': this.referrer
                    });
                } catch (e) {
                    console.error(e.message);
                }

                location.href = '/checkout-success/thank-you-' + payload.type + '/' + btoa(JSON.stringify({data: response.data}));

            }).catch((error) => {
                this.errorNotify('');
            }).finally(() => {
                this.disableLoader();
            });

        },

        initApplePaySession: function () {
            if (window.ApplePaySession === undefined) {
                let appleScript = document.createElement("script");
                appleScript.setAttribute(
                    "src",
                    `https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js`
                );
                document.head.appendChild(appleScript);
                appleScript.onload = () => {
                    // console.log('LOAD: apple script after append');
                    this.canApplePayment = this.applePayAvailable();
                };
            } else {
                // console.log('LOAD: apple script from cash');
                this.canApplePayment = this.applePayAvailable();
            }
        },
        /**
         * Checks if Apple Pay is possible in the current environment.
         * @return {boolean} Boolean to check if Apple Pay is possible
         */
        applePayAvailable: function () {
            return (
                window.ApplePaySession &&
                ApplePaySession.canMakePayments()
            );
        },
        /**
         * Sets a onClick listen on the Apple Pay button. When clicked it will
         * begin the Apple Pay session with your configuration
         */
        setButtonClickListener: function () {
            this.startApplePaySession({
                currencyCode: this.shop.shop_localisation.currencyCode,
                countryCode: this.shop.shop_localisation.countryCode,
                merchantCapabilities: [
                    "supports3DS",
                    "supportsEMV",
                    "supportsCredit",
                    "supportsDebit"
                ],
                supportedNetworks: this.payments.acceptedCardSchemes,
                shippingType: "shipping",
                requiredBillingContactFields: [
                    "postalAddress",
                    "name",
                    "phone",
                    "email"
                ],
                requiredShippingContactFields: [
                    "postalAddress",
                    "name",
                    "phone",
                    "email"
                ],
                total: {
                    label: this.shop.shop_name,
                    amount: this.shop.product_price,
                    type: "final"
                }
            });
        },
        /**
         * Starts the Apple Pay session using a configuration
         */
        startApplePaySession: function (config) {
            const applePaySession = new window.ApplePaySession(6, config);
            this.handleApplePayEvents(applePaySession);
            applePaySession.begin();
        },

        /**
         * This is the main method of the script, since here we handle all the Apple Pay
         * events. Here you are able to populate your shipping methods, react to  shipping methods
         * changes, and many other interaction that the user has with the Apple Pay pup-up.
         *
         * @param {object} appleSession (the one generate on the button click)
         *
         */
        handleApplePayEvents: function (appleSession) {
            const app = this;
            // This is the first event that Apple triggers. Here you need to validate the
            // Apple Pay Session from your Back-End
            appleSession.onvalidatemerchant = function (event) {
                app.validateApplePaySession(event.validationURL, function (
                    merchantSession
                ) {
                    appleSession.completeMerchantValidation(merchantSession);
                });
            };
            // This method is triggered before populating the shipping methods. This is the
            // perfect place inject your shipping methods
            appleSession.onshippingcontactselected = function (event) {
                // populate with the availbale shipping methods for the region (Apple will tell you the region).
                // while the full address will only be available to you after the user confirms tha payment
                const update = {};
                appleSession.completeShippingContactSelection(update);
            };

            // This method is the most important method. It gets triggered after the user has
            // confirmed the transaction with the Touch ID or Face ID. Besides getting all the
            // details about the customer (email, address ...) you also get the Apple Pay payload
            // needed to perform a payment.
            appleSession.onpaymentauthorized = function (event) {
                app.performTransaction(event.payment, function (outcome) {
                    if (outcome.approved) {
                        appleSession.completePayment(
                            window.ApplePaySession.STATUS_SUCCESS
                        );
                        console.log(outcome);
                    } else {
                        appleSession.completePayment(
                            window.ApplePaySession.STATUS_FAILURE
                        );
                        console.log(outcome);
                    }
                });
            };

            appleSession.oncancel = event => {
                alert("Payment cancelled by WebKit");
            };
        },

        /**
         * This method cals your backend server with the Apple Pay validation URL.
         * On the backend, a POST request will be done to this URL with the Apple Pay certificates
         * and the outcome will be returned
         *
         * @param {string} appleUrl The Apple Pay validation URL generated by Apple
         * @param {function} callback Callback function used to return the server call outcome
         *
         * @return {object} The session payload
         *
         */
        validateApplePaySession: function (appleUrl, callback) {
            console.log("HTTP ACTION: BACKEND_URL_VALIDATE_SESSION");

            if (this.refContactInformation) {
                if (!this.refContactInformation.validate()) {
                    return false;
                }
            } else {
                if (!this.$parent.validate()) {
                    return false;
                }
            }

            new ApiRequest().applePayValidateSession({appleUrl}).then((response) => {
                callback(response.data)
            }).catch((error) => {
                console.error(error);
            });

            // axios
            // .post(
            //     BACKEND_URL_VALIDATE_SESSION,
            //     {
            //         appleUrl
            //     },
            //     {
            //         headers: { 'Access-Control-Allow-Origin': '*' }
            //     }
            // )
            // .then(function (response) {
            //     callback(response.data)
            // })
        },

        /**
         * This method returns the available payment methods for a certain region. You can add
         * your business logic here to determine the shipping methods you need.
         *
         * @param {string} 2 Letter ISO of the region
         *
         * @return {Array} An array of shipping methods
         *
         */
        // getAvailableShippingMethods: function (region) {
        //     // return the shipping methods available based on region
        //     if (region === "US") {
        //         return {methods: config.shipping.US_region};
        //     } else {
        //         return {methods: config.shipping.WORLDWIDE_region};
        //     }
        // },

        // calculateTotal: function (subtotal, shipping) {
        //     return (parseFloat(subtotal) + parseFloat(shipping)).toFixed(2);
        // },

        // here we talk to our backend to send the Apple Pay Payload and return the transaction outcome
        performTransaction: function (details, callback) {
            // I'm using AXIOS to do a POST request to the backend but any HTTP client can be used
            console.log("HTTP ACTION: BACKEND_URL_PAY");
            // this.createOrder(details.token)
            // axios
            // .post(
            //     BACKEND_URL_PAY,
            //     {
            //         token: details.token,
            //         customerEmail: details.shippingContact.emailAddress,
            //         billingDetails: details.billingContact,
            //         shippingDetails: details.shippingContact
            //     },
            //     {
            //         headers: { 'Access-Control-Allow-Origin': '*' }
            //     }
            // )
            // .then(function (response) {
            //     callback(response.data)
            // })
            callback({
                some: "some"
            });
        }

    }
};
</script>

<style>
.apple-pay-btn-container {
    padding-top: 11px;
    padding-bottom: 11px;
}

@supports (-webkit-appearance: -apple-pay-button) {
    .apple-pay-button {
        display: inline-block;
        -webkit-appearance: -apple-pay-button;
    }

    .apple-pay-button-black {
        -apple-pay-button-style: black;
        width: 100%;
        min-width: 200px;
        min-height: 32px;
        max-height: 55px;
        height: 55px;
    }

    .apple-pay-button-white {
        -apple-pay-button-style: white;
        width: 100%;
        min-width: 200px;
        min-height: 32px;
        max-height: 55px;
        height: 55px;
    }

    .apple-pay-button-white-with-line {
        -apple-pay-button-style: white-outline;
        width: 100%;
        min-width: 200px;
        min-height: 32px;
        max-height: 55px;
        height: 55px;
    }
}

@supports not (-webkit-appearance: -apple-pay-button) {
    .apple-pay-button {
        display: inline-block;
        background-size: 100% 60%;
        background-repeat: no-repeat;
        background-position: 50% 50%;
        border-radius: 5px;
        padding: 0;
        box-sizing: border-box;
        width: 100%;
        min-width: 200px;
        min-height: 32px;
        max-height: 55px;
        height: 55px;
        cursor: pointer;
    }

    .apple-pay-button-black {
        background-image: -webkit-named-image(apple-pay-logo-white);
        background-color: black;
    }

    .apple-pay-button-white {
        background-image: -webkit-named-image(apple-pay-logo-black);
        background-color: white;
    }

    .apple-pay-button-white-with-line {
        background-image: -webkit-named-image(apple-pay-logo-black);
        background-color: white;
        border: 0.0357rem solid black;
    }
}
</style>
